import { useState, useEffect, useRef } from 'react';
import { Search, X, PlusCircle, Edit, Trash2, Copy, Check, Sparkles, RotateCcw, Upload, HelpCircle } from 'lucide-react';
import { API_ENDPOINTS } from '../config';

export default function SnippetModal({ isOpen, onClose, snippets, initialText, onSubmit, onCreate, onEdit, onDelete, onUse, mode, onImportSuccess, showNotification }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [questionText, setQuestionText] = useState(initialText);
  const [hoveredSnippet, setHoveredSnippet] = useState(null);
  const [copied, setCopied] = useState(false);
  const [autoRanked, setAutoRanked] = useState(false);
  const [rankedSnippets, setRankedSnippets] = useState([]);
  const [autoRankLoading, setAutoRankLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showImportHelp, setShowImportHelp] = useState(false);
  const [includePublic, setIncludePublic] = useState(() => {
    // Load preference from localStorage (default: false)
    const saved = localStorage.getItem('includePublicFootnotes');
    return saved === 'true';
  });
  const fileInputRef = useRef(null);
  const helpTooltipRef = useRef(null);

  const handleCopy = async () => {
    if (!questionText) return;
    try {
      await navigator.clipboard.writeText(questionText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleIncludePublicChange = (checked) => {
    setIncludePublic(checked);
    localStorage.setItem('includePublicFootnotes', checked.toString());
  };

  useEffect(() => {
    if (isOpen) {
      setQuestionText(initialText);
      setSearchQuery('');
      setAutoRanked(false);
      setRankedSnippets([]);
      setShowImportHelp(false);
    }
  }, [isOpen, initialText]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showImportHelp && helpTooltipRef.current && !helpTooltipRef.current.contains(e.target)) {
        setShowImportHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showImportHelp]);

  if (!isOpen) return null;

  const handleAutoRank = async () => {
    if (!questionText || questionText.trim() === '') {
      alert('Auto ranking requires a user question. Please type your question first.');
      return;
    }

    setAutoRankLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await fetch(`${API_ENDPOINTS.main}/footnotes/auto-rank`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_question: questionText,
          mode: mode || 'virgo',
          include_public: includePublic
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to auto-rank footnotes');
      }

      const data = await response.json();
      setRankedSnippets(data.footnotes);
      setAutoRanked(true);
      // Keep search query so ranked results get filtered
    } catch (error) {
      console.error('Auto-rank error:', error);
      alert(error.message || 'Failed to auto-rank footnotes');
    } finally {
      setAutoRankLoading(false);
    }
  };

  const handleClearRanking = () => {
    setAutoRanked(false);
    setRankedSnippets([]);
    setSearchQuery('');
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    event.target.value = '';

    if (!file.name.endsWith('.csv')) {
      showNotification('error', 'Please upload a CSV file');
      return;
    }

    setImporting(true);

    try {
      const token = localStorage.getItem('token');

      // Upload file to bulk import endpoint
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_ENDPOINTS.main}/footnotes/bulk-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to import footnotes');
      }

      const result = await response.json();

      // Build summary message
      let summaryMessage = '';
      if (result.imported > 0) summaryMessage += `✓ Imported ${result.imported} footnote${result.imported !== 1 ? 's' : ''}`;
      if (result.skipped > 0) summaryMessage += `${summaryMessage ? ', ' : ''}⊘ Skipped ${result.skipped} duplicate${result.skipped !== 1 ? 's' : ''}`;
      if (result.errors > 0) summaryMessage += `${summaryMessage ? ', ' : ''}✗ ${result.errors} error${result.errors !== 1 ? 's' : ''}`;

      if (result.errors > 0 && result.error_details?.length > 0) {
        console.error('Import errors:', result.error_details);
        showNotification(result.imported > 0 ? 'success' : 'error', `${summaryMessage}. Check console for details.`);
      } else {
        showNotification('success', summaryMessage || 'Import completed');
      }

      // Refresh snippets list
      if (result.imported > 0 && onImportSuccess) {
        await onImportSuccess();
      }

    } catch (error) {
      console.error('Import error:', error);
      showNotification('error', error.message || 'Failed to import footnotes');
    } finally {
      setImporting(false);
    }
  };

  const filteredSnippets = (() => {
    // Start with either ranked or unranked snippets
    let baseSnippets = autoRanked ? rankedSnippets : snippets;

    // Apply scoping: filter out public footnotes if includePublic is false
    if (!includePublic) {
      baseSnippets = baseSnippets.filter(snippet => {
        const level = snippet.level || 'private';
        return level !== 'public';
      });
    }

    // Apply search filter to both ranked and unranked results
    if (!searchQuery.trim()) {
      return baseSnippets;
    }

    const query = searchQuery.toLowerCase();
    return baseSnippets.filter(snippet =>
      snippet.title.toLowerCase().includes(query) ||
      snippet.content.toLowerCase().includes(query) ||
      snippet.tags.some(tag => tag.toLowerCase().includes(query))
    );
  })();

  const handleSnippetClick = (snippet) => {
    setQuestionText(prev => {
      const trimmed = prev.trim();
      return trimmed ? `${trimmed}\n\n${snippet.content}` : snippet.content;
    });

    // Track snippet usage
    if (onUse) {
      onUse(snippet.id);
    }
  };

  const handleAccept = () => {
    if (questionText.trim()) {
      onSubmit(questionText);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-6xl w-full h-[90vh] flex flex-col border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Add Context to Question</h2>
          <div className="flex items-center gap-2">
            <div className="relative flex items-center gap-1">
              <button
                onClick={handleImportClick}
                disabled={importing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Import footnotes from CSV"
              >
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">{importing ? 'Importing...' : 'Import'}</span>
              </button>
              <div ref={helpTooltipRef} className="relative">
                <button
                  onClick={() => setShowImportHelp(!showImportHelp)}
                  className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                  title="CSV format help"
                >
                  <HelpCircle className="w-4 h-4" />
                </button>
                {showImportHelp && (
                  <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-10">
                    <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2">CSV Format Requirements</h4>
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                      <p className="font-medium">Required columns:</p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li><code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">title</code> - Footnote title</li>
                        <li><code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">content</code> - Full content/instructions</li>
                        <li><code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">tags</code> - Semicolon-separated tags</li>
                        <li><code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">mode</code> - "virgo", "taurus", or "both"</li>
                        <li><code className="bg-gray-100 dark:bg-gray-900 px-1 py-0.5 rounded">level</code> - "private", "organization", or "public"</li>
                      </ul>
                      <p className="mt-2 text-gray-700 dark:text-gray-300">
                        💡 <strong>Tip:</strong> See <a href="https://app.verbagpt.com/downloads/sample_questions.csv" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">sample_questions.csv</a> for format examples
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onCreate}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              title="Create new footnote"
            >
              <PlusCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Create</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Content */}
        <div className="flex-1 flex gap-6 p-6 overflow-hidden">
          {/* Left side - Editable text */}
          <div className="flex-1 flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Your Question (with added context)</label>
            <textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="Type your question or click snippets to add context..."
              className="flex-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-4 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono"
            />
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                title="Copy to clipboard"
              >
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                <span className="text-sm font-medium">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              <button
                onClick={handleAccept}
                disabled={!questionText.trim()}
                className="flex-1 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Accept & Send
              </button>
            </div>
          </div>

          {/* Right side - Snippet list */}
          <div className="flex-1 flex flex-col gap-3">
            {/* Header with inline scoping checkbox */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Quick Footnotes</label>
                <span className="px-1.5 py-0.5 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-medium">
                  {filteredSnippets.length}
                </span>
              </div>
              <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 cursor-pointer hover:text-gray-900 dark:hover:text-gray-200">
                <input
                  type="checkbox"
                  checked={includePublic}
                  onChange={(e) => handleIncludePublicChange(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-0"
                />
                <span>Include public</span>
              </label>
            </div>

            {/* Search and Auto Rank - inline */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={autoRanked ? "Filter ranked results..." : "Search snippets..."}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              {!autoRanked ? (
                <button
                  onClick={handleAutoRank}
                  disabled={autoRankLoading}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                    mode === 'taurus'
                      ? 'bg-amber-600 hover:bg-amber-700'
                      : 'bg-purple-600 hover:bg-purple-700'
                  } text-white transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap`}
                  title="Rank footnotes by semantic relevance to your question"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>{autoRankLoading ? 'Ranking...' : 'Auto Rank'}</span>
                </button>
              ) : (
                <button
                  onClick={handleClearRanking}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white transition-colors text-sm font-medium whitespace-nowrap"
                  title="Clear ranking and show all footnotes"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Clear</span>
                </button>
              )}
            </div>

            {/* Snippet tiles */}
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {filteredSnippets.map((snippet) => {
                // Determine background color based on privacy level
                const level = snippet.level || 'private';
                const bgClasses = level === 'demo'
                  ? 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30'
                  : level === 'public'
                  ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                  : level === 'organization'
                  ? 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30'
                  : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700';

                return (
                <div
                  key={snippet.id}
                  className="relative group"
                  onMouseEnter={() => setHoveredSnippet(snippet.id)}
                  onMouseLeave={() => setHoveredSnippet(null)}
                >
                  <button
                    onClick={() => handleSnippetClick(snippet)}
                    className={`w-full text-left p-4 rounded-xl border border-gray-200 dark:border-gray-700 ${bgClasses} transition-colors`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{snippet.title}</div>
                      {autoRanked && snippet.similarity !== undefined && (
                        <span className="ml-2 px-2 py-0.5 rounded-md bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-xs font-bold whitespace-nowrap">
                          {snippet.similarity}% match
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                      {snippet.content}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {snippet.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </button>
                  {/* Edit and Delete buttons - appear on hover (only for editable footnotes) */}
                  {hoveredSnippet === snippet.id && snippet.is_editable !== false && (
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(snippet);
                        }}
                        className="p-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900 hover:border-blue-400 text-blue-600 dark:text-blue-400 transition-colors shadow-sm"
                        title="Edit footnote"
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(snippet.id);
                        }}
                        className="p-1.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900 hover:border-red-400 text-red-600 dark:text-red-400 transition-colors shadow-sm"
                        title="Delete footnote"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              );
              })}
              {filteredSnippets.length === 0 && (
                <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                  No footnotes found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
